import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export type AccordionEntry = {
    id: string
    title: ReactNode
    content: ReactNode
}

interface AccordionProps {
    items: AccordionEntry[]
    defaultOpen?: string[]
    multiple?: boolean
    className?: string
}

export const Accordion: FC<AccordionProps> = ({
    items,
    defaultOpen = [],
    multiple = false,
    className
}): ReactNode => {
    const [open, setOpen] = useState<string[]>(defaultOpen)

    const toggle = (id: string) => {
        setOpen(current => {
            if (current.includes(id)) return current.filter(item => item !== id)

            return multiple ? [...current, id] : [id]
        })
    }

    return (
        <div className={cn('divide-y divide-border overflow-hidden rounded-xl border border-border bg-card shadow-card', className)}>
            {items.map(item => {
                const isOpen = open.includes(item.id)
                const buttonId = `accordion-trigger-${item.id}`
                const panelId = `accordion-panel-${item.id}`

                return (
                    <div key={item.id} data-open={isOpen} className='group'>
                        <h3>
                            <button
                                id={buttonId}
                                type='button'
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => toggle(item.id)}
                                className='flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-start text-body font-semibold text-navy transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/45 group-data-[open=true]:text-primary-deep'
                            >
                                <span>{item.title}</span>

                                <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground transition-[transform,background-color,color] duration-200 group-data-[open=true]:rotate-180 group-data-[open=true]:bg-primary-soft group-data-[open=true]:text-primary-deep'>
                                    <ChevronDown className='size-4' aria-hidden='true' />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={panelId}
                            role='region'
                            aria-labelledby={buttonId}
                            hidden={!isOpen}
                            className='px-5 pb-5 text-body text-muted-foreground animate-in fade-in slide-in-from-top-1 duration-200'
                        >
                            {item.content}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
